<script setup lang="ts">
import {ref, watch} from 'vue';
import Author from "~/repository/models/Author";

const props = defineProps({
  author: Author,
  avatar: String,
});

const emit = defineEmits(['update:selectedPlan']);

const selectedValue = ref('monthly');
const isOpen = ref(false);

const staticMonthlyPrice = ref(props.author?.subscription.monthly);
const staticYearlyPrice = ref(props.author?.subscription.yearly);

// Gross price (total) based on selected plan (monthly/yearly)
const grossPrice = computed(() => {
  return selectedValue.value === 'monthly' ? staticMonthlyPrice.value : staticYearlyPrice.value;
});

const subtotal = ref<number>(0); // net price (without VAT) in cents
const vat = ref<number>(0);      // VAT amount in cents
const total = ref<number>(0);    // gross price (with VAT) in cents

// Get tax store
const taxStore = useTaxStore();
const {t} = useI18n();

// Get tax rate directly from store
// - using rates?.subscription instead of taxRate?.subscription
const taxRateValue = computed(() => {
  const taxRate = taxStore.rates?.subscription;
  // Use 30% when rate is null or undefined
  if (taxRate === null || taxRate === undefined) return 0.3;
  return taxRate; // Return actual rate (0 if explicitly set to 0)
});

const vatLabel = computed(() => {
  if (taxRateValue.value === 0) return 'VAT (0%)';
  return `VAT (${(taxRateValue.value * 100).toFixed(0)}%)`;
});

// Flag to determine if we should show the tax line
const showTaxLine = computed(() => taxRateValue.value > 0);

const updateVATandSubtotal = (grossPriceInCents: number): void => {
  if (!grossPriceInCents) {
    subtotal.value = 0;
    vat.value = 0;
    total.value = 0;
    return;
  }

  // Convert cents to currency for easier calculations
  const grossPriceInCurrency = grossPriceInCents / 100;

  // If VAT rate is 0%, subtotal equals gross price and VAT is 0
  if (taxRateValue.value === 0) {
    subtotal.value = grossPriceInCents;
    vat.value = 0;
    total.value = grossPriceInCents;
    return;
  }

  // Calculate net price from gross price
  const netPriceInCurrency = Number((grossPriceInCurrency / (1 + taxRateValue.value)).toFixed(2));

  // Calculate VAT amount
  const vatAmountInCurrency = Number((grossPriceInCurrency - netPriceInCurrency).toFixed(2));

  // Convert back to cents for the interface
  subtotal.value = Math.round(netPriceInCurrency * 100);
  vat.value = Math.round(vatAmountInCurrency * 100);
  total.value = grossPriceInCents; // Total is the original gross price
};

// Initialize values
updateVATandSubtotal(grossPrice.value);

// Watch for changes in selected plan
watch(selectedValue, (newValue) => {
  emit('update:selectedPlan', newValue);
});

// Watch for changes in gross price and update values
watch(grossPrice, (newGrossPrice) => {
  updateVATandSubtotal(newGrossPrice);
});


const toggleDetails = () => {
  isOpen.value = !isOpen.value;
};

const closeDetails = (event: Event) => {
  const detailsElement = document.querySelector('#details-element');
  if (detailsElement && !detailsElement.contains(event.target as Node)) {
    isOpen.value = false;
  }
};

</script>

<template>
  <div>
    <div v-if="isOpen" class="inset-0 absolute z-[49] bg-[rgba(0,0,0,0.25)]" @click="closeDetails"></div>
    <div
      id="details-element"
      :class="{ 'clipped-section': true, 'visible-modal': isOpen }"
      style="box-shadow: 0 24px 32px rgba(0, 0, 0, .15);"
      class="bg-popover"
      @click.stop
    >
      <div
        class="grid grid-cols-[100px_auto_100px] items-center border-b-[0.5px] border-gray-400 py-3 px-4 md:px-6">
        <button @click="isOpen = false" class="flex items-center justify-start gap-1.5 text-gray">
          {{ t('buttons.cancel') }}
          <span class="-rotate-90 mt-0.5">
              <IconChevronRight size="14px"/>
            </span>
        </button>
        <h3 class="font-semibold text-base  text-center">{{ $t('payment.summary.subtitle') }}</h3>
      </div>
      <div class="p-4 md:p-6">
        <div class="flex justify-between items-start py-4">
          <div>
            <NuxtLink class="font-semibold text-black">
              <span class="text-gradient "
                    style="-webkit-background-clip: text;-webkit-text-fill-color: transparent;">@{{ author?.username || ''
                }}</span>
            </NuxtLink>
            <div class="text-gray text-xs">
              <div>{{ t('members.subscribe_info') }}</div>
              <div class="text-gray mt-1">
                {{ selectedValue === 'monthly' ? t('members.monthly_billing') : t('members.annual_billing') }}

              </div>
            </div>
          </div>
          <div class="text-foreground font-semibold">{{ (subtotal / 100).toFixed(2) }}
            {{ author?.subscription?.currency_code?.toUpperCase() || '' }}
          </div>
        </div>
        <div class="border-t border-gray-50">
          <div class="flex justify-between items-start py-4">
            <div class="text-foreground font-semibold">{{ $t('payment.subtotal') }}</div>
            <div class="text-foreground font-semibold">{{ (subtotal / 100).toFixed(2) }}
              {{ author?.subscription?.currency_code?.toUpperCase() || '' }}
            </div>
          </div>
          <div v-if="showTaxLine" class="flex justify-between items-start pb-4">
            <div class="text-foreground">{{ vatLabel }}</div>
            <div class="text-gray">{{ (vat / 100).toFixed(2) }}
              {{ author?.subscription?.currency_code?.toUpperCase() || '' }}
            </div>
          </div>
        </div>
        <div class="border-t border-gray-50">
          <div class="flex justify-between items-end pt-4">
            <div class="text-foreground font-semibold">{{ $t('payment.total_due') }}</div>
            <div class="text-foreground text-lg font-semibold">
              {{ (total / 100).toFixed(2) }} {{ author?.subscription?.currency_code?.toUpperCase() || '' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="p-4 md:p-6">
      <div class="flex items-center justify-center gap-3 mb-4 md:mb-6">
        <Avatar class="w-12 h-12">
          <AvatarImage :src="avatar" alt="@radix-vue"/>
        </Avatar>
        <div class="flex flex-col">
           <span class="text-gradient font-semibold text-lg"
                 style="-webkit-background-clip: text;-webkit-text-fill-color: transparent;">@{{ author?.username || ''
             }}</span>
          <span class="text-xs font-medium">{{ $t('purchase.subscription_purchase') }}</span>
        </div>

      </div>

      <!--      <div class="flex items-end justify-center gap-1.5 mb-4">-->
      <!--        <span class="font-semibold text-white text-2xl md:text-3xl">-->
      <!--          {{ (total / 100).toFixed(2) }} {{ author?.subscription?.currency_code?.toUpperCase() || '' }}-->
      <!--        </span>-->
      <!--        <span class="font-semibold text-gray"> -->
      <!--          {{ selectedValue === 'monthly' ? 'na miesiąc' : 'na rok' }}-->
      <!--        </span>-->
      <!--      </div>-->
      <RadioGroup class="grid" v-model="selectedValue">
        <div
          :class="[
            'flex items-center border-background rounded-xl border-[1.5px] pl-4',
            selectedValue === 'monthly' ? 'border-[0.5px] border-input bg-neutral-100' : 'border-gray-50 opacity-50'
          ]"
        >
          <RadioGroupItem id="r1" class="bg-background" value="monthly"/>
          <Label for="r1" class="cursor-pointer select-none relative inset-0 flex justify-start w-full p-4 pl-2">
            <div class="flex flex-col">
              <span class="font-semibold mb-1">{{ $t('subscription.monthly') }}</span>
              <span class="text-gray text-xs">
                {{ selectedValue === 'monthly' ? (total / 100).toFixed(2) : (staticMonthlyPrice / 100).toFixed(2)
                }} {{ author?.subscription?.currency_code?.toUpperCase() || '' }}/{{ $t('members.monthly_billing') }}
              </span>
            </div>
            <!--            <div class="ml-auto pl-4 text-lg md:text-xl font-semibold flex items-center">-->
            <!--              &lt;!&ndash;                          {{ (staticMonthlyPrice / 100).toFixed(2) }} {{ author?.subscription?.currency_code?.toUpperCase() || '' }}&ndash;&gt;-->
            <!--&lt;!&ndash;              Polecamy&ndash;&gt;-->
            <!--            </div>-->
          </Label>
        </div>
        <div
          :class="[
            'flex items-center border-background rounded-xl border-[1.5px] pl-4',
            selectedValue === 'yearly' ? 'border-[0.5px] border-input bg-neutral-100' : 'border-gray-50'
          ]"
        >
          <RadioGroupItem id="r2" class="bg-background" value="yearly"/>
          <Label for="r2" class="cursor-pointer select-none relative inset-0 flex justify-center w-full p-4 pl-2">
            <div class="flex flex-col" :class="{ 'opacity-50': selectedValue !== 'yearly' }">
              <div class="mb-0.5 flex">
                <span class="font-semibold mb-1">{{ $t('subscription.yearly') }}</span>
                <span class="ml-1 whitespace-nowrap">({{ $t('subscription.save_10') }})</span>
              </div>
              <span class="text-gray text-xs">
                {{ selectedValue === 'yearly' ? ((total / 12) / 100).toFixed(2) : ((staticYearlyPrice / 12) / 100).toFixed(2)
                }} {{ author?.subscription?.currency_code?.toUpperCase() || '' }}/{{ $t('members.monthly_billing') }}
              </span>
            </div>
            <!--                        <div class="ml-auto pl-4 max-md:whitespace-nowrap flex items-center text-lg md:text-xl font-semibold">-->
            <!--                          {{ (staticYearlyPrice / 100).toFixed(2) }}-->
            <!--                          {{ author?.subscription?.currency_code?.toUpperCase() || '' }}-->
            <!--                        </div>-->
            <div
              class="ml-auto my-auto h-7 text-white text-xs bg-gradient-button font-bold rounded-full flex items-center px-3 py-1">
              {{ $t('subscription.most_popular') }}
            </div>
          </Label>
        </div>
      </RadioGroup>

      <Button @click="toggleDetails" class="bg-indigo-950 text-white w-1/2 mx-auto flex h-10 mt-3 gap-1.5 items-center">
        {{ $t('buttons.view_details') }}
        <span class="rotate-90 mt-0.5">
          <IconChevronRight size="14px"/>
        </span>
      </Button>
    </div>
  </div>
</template>

<style scoped>
.clipped-section {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  transform: translateY(-100%);
  opacity: 0;
  transition: transform 0.2s ease-in-out, opacity 0.2s ease-in-out;
}

.clipped-section.visible-modal {
  transform: translateY(0);
  opacity: 1;
}
</style>
